import { addressSearchProps } from '@/core/utils/address-search';
import { useProvinceWards, wardLabel } from '@/app/home/components/address/useProvinceWards';
import { ConfigProvider, Form, Input, message, Modal, Select, InputNumber } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import { AddressRepo } from "@/core/repos/address.repo";
import { PaymentPointRepo } from "@/core/repos/tables/payment-point.repo";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { PaymentPointModel } from "@/core/models/payment-point.model";

const addressRepo = container.get(AddressRepo);
const paymentPointRepo = container.get(PaymentPointRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: PaymentPointModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<PaymentPointModel>();
    const { provinces, wards, loadAddress, handleProvinceChange, handleWardChange } = useProvinceWards(form, addressRepo);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();

    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        const values = data;
        form.setFieldsValue(values);
        setIsEdit(!!data.id);
        await loadAddress(values);
    };
    useEffect(() => {
        handleDataChange();
    }, [data]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            // Ensure longitude and workingHours are included even if undefined
            const submitData: PaymentPointModel = {
                ...data,
                ...formData,
                longtitude: formData.longtitude ?? data.longtitude ?? undefined,
                workingHours: formData.workingHours ?? data.workingHours ?? undefined,
            };
            if (isEdit) {
                submitData.id = data.id;
                const result = await paymentPointRepo.update(submitData);
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật điểm thanh toán"
                    });
                } else {
                    messageApi.error({
                        content: "Cập nhật điểm thanh toán thất bại"
                    });
                }
            } else {
                const result = await paymentPointRepo.create(submitData);
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Thêm điểm thanh toán mới thành công"
                    });
                } else {
                    messageApi.error({
                        content: "Thêm điểm thanh toán mới thất bại"
                    });
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={980} 
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT ĐIỂM THANH TOÁN</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<PaymentPointModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit && !permissionStore.page.edit}
                    >
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<PaymentPointModel>
                                label="Tên điểm thanh toán"
                                name="name"
                                rules={[{ required: true, message: 'Vui lòng nhập tên điểm thanh toán!' }]}
                            >
                                <Input placeholder="Nhập tên điểm thanh toán" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Số điện thoại"
                                name="phone"
                            >
                                <Input placeholder="Nhập số điện thoại" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Địa chỉ"
                                name="address"
                                rules={[{ required: true, message: 'Vui lòng nhập địa chỉ!' }]}
                            >
                                <Input placeholder="Nhập địa chỉ" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Trạng thái"
                                name="status"
                                rules={[{ required: true, message: 'Vui lòng chọn trạng thái!' }]}
                            >
                                <Select placeholder="Chọn trạng thái" style={{width: INPUT_WIDTH}}>
                                    <Select.Option value="Active">Hoạt động</Select.Option>
                                    <Select.Option value="Inactive">Tạm ngưng</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Tỉnh/ TP"
                                name="provinceId"
                            >
                                <Select 
                                    {...addressSearchProps}
                                    placeholder="Chọn Tỉnh/ TP" 
                                    style={{width: INPUT_WIDTH}} 
                                    onChange={handleProvinceChange}
                                    allowClear
                                >
                                    {provinces.map(province => (
                                        <Select.Option key={province.id} value={province.id}>
                                            {province.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Vĩ độ"
                                name="latitude"
                            >
                                <InputNumber 
                                    placeholder="Nhập vĩ độ" 
                                    style={{width: INPUT_WIDTH}}
                                    precision={6}
                                ></InputNumber>
                            </Form.Item>
                            <Form.Item<PaymentPointModel> name="districtId" hidden><Input /></Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Kinh độ"
                                name="longtitude"
                            >
                                <InputNumber 
                                    placeholder="Nhập kinh độ" 
                                    style={{width: INPUT_WIDTH}}
                                    precision={6}
                                ></InputNumber>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Xã/ Phường"
                                name="wardId"
                            >
                                <Select {...addressSearchProps} placeholder="Chọn Xã/ Phường" style={{width: INPUT_WIDTH}} allowClear onChange={handleWardChange}>
                                    {wards.map(ward => (
                                        <Select.Option key={ward.id} value={ward.id}>
                                            {wardLabel(ward)}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Mô tả"
                                name="description"
                            >
                                <Input.TextArea 
                                    placeholder="Nhập mô tả" 
                                    style={{width: INPUT_WIDTH}}
                                    rows={3}
                                ></Input.TextArea>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Giờ làm việc"
                                name="workingHours"
                            >
                                <Input 
                                    placeholder="Nhập giờ làm việc (VD: 8:00 - 17:00)" 
                                    style={{width: INPUT_WIDTH}}
                                ></Input>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};

